import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { db } from "@/lib/db";
import { syncAllSettings } from "@/lib/settingsSync";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Sun,
  Moon,
  Monitor,
  Globe,
  Download,
  LogOut,
  Check,
  CheckCircle,
  ChevronDown,
  Smartphone,
  X,
  Bug,
  BookOpen,
  Clock,
} from "lucide-react";
import { VOW_CATEGORIES } from "@/data/vows";
import { cn } from "@/lib/utils";

type Theme = "light" | "dark" | "system";


interface DbDiag {
  totalEntries: number;
  todayEntries: number;
  todayDate: string;
  uniqueDates: { date: string; userId: string; count: number }[];
}

export function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { isInstalled, isIOS, canInstall, install } = useInstallPrompt();
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [diag, setDiag] = useState<DbDiag | null>(null);

  // Kontinuierlicher Wechsel — Tugend
  const [tugendRotationEnabled, setTugendRotationEnabled] = useState(
    () => localStorage.getItem("vajra-tugend-rotation-enabled") !== "false" // default true
  );

  // Kontinuierlicher Wechsel — Gelübde (ehemals "Gelübde im Zeitslot")
  const [vowSlotEnabled, setVowSlotEnabled] = useState(
    () => localStorage.getItem("vajra-vow-slot-enabled") === "true"
  );
  const [vowSlotCount, setVowSlotCount] = useState(
    () => parseInt(localStorage.getItem("vajra-vow-slot-count") || "3", 10)
  );
  const [vowSlotOrder, setVowSlotOrder] = useState<"sequential" | "random">(
    () => (localStorage.getItem("vajra-vow-slot-order") || "sequential") as "sequential" | "random"
  );
  const [vowSlotCategories, setVowSlotCategories] = useState<string[]>(
    () => JSON.parse(localStorage.getItem("vajra-vow-slot-categories") || "[]")
  );

  // Re-read state when Supabase settings are loaded
  useEffect(() => {
    function onSettingsLoaded() {
      setTugendRotationEnabled(localStorage.getItem("vajra-tugend-rotation-enabled") !== "false");
      setVowSlotEnabled(localStorage.getItem("vajra-vow-slot-enabled") === "true");
      setVowSlotCount(parseInt(localStorage.getItem("vajra-vow-slot-count") || "3", 10));
      setVowSlotOrder((localStorage.getItem("vajra-vow-slot-order") || "sequential") as "sequential" | "random");
      setVowSlotCategories(JSON.parse(localStorage.getItem("vajra-vow-slot-categories") || "[]"));
    }
    window.addEventListener("vajra-settings-loaded", onSettingsLoaded);
    return () => window.removeEventListener("vajra-settings-loaded", onSettingsLoaded);
  }, []);

  function updateTugendRotationEnabled(val: boolean) {
    setTugendRotationEnabled(val);
    localStorage.setItem("vajra-tugend-rotation-enabled", String(val));
    if (user?.id) syncAllSettings(user.id);
  }

  function updateVowSlotEnabled(val: boolean) {
    setVowSlotEnabled(val);
    localStorage.setItem("vajra-vow-slot-enabled", String(val));
    if (user?.id) syncAllSettings(user.id);
  }
  function updateVowSlotCount(val: number) {
    setVowSlotCount(val);
    localStorage.setItem("vajra-vow-slot-count", String(val));
    if (user?.id) syncAllSettings(user.id);
  }
  function updateVowSlotOrder(val: "sequential" | "random") {
    setVowSlotOrder(val);
    localStorage.setItem("vajra-vow-slot-order", val);
    if (user?.id) syncAllSettings(user.id);
  }
  function updateVowSlotCategories(cats: string[]) {
    setVowSlotCategories(cats);
    localStorage.setItem("vajra-vow-slot-categories", JSON.stringify(cats));
    if (user?.id) syncAllSettings(user.id);
  }

  useEffect(() => {
    if (!showDebug) return;
    async function loadDiag() {
      const d = new Date();
      const todayDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const allEntries = await db.practiceEntries.toArray();
      const todayCount = allEntries.filter((e) => e.date === todayDate).length;
      // Group by date+userId
      const grouped: Record<string, { date: string; userId: string; count: number }> = {};
      for (const e of allEntries) {
        const key = `${e.userId}|${e.date}`;
        if (!grouped[key]) grouped[key] = { date: e.date, userId: e.userId, count: 0 };
        grouped[key].count++;
      }
      const uniqueDates = Object.values(grouped).sort((a, b) => b.date.localeCompare(a.date));
      setDiag({ totalEntries: allEntries.length, todayEntries: todayCount, todayDate, uniqueDates });
    }
    loadDiag();
  }, [showDebug]);

  const currentLang = i18n.language?.startsWith("en") ? "en" : "de";

  const themeOptions: { value: Theme; label: string; icon: React.ReactNode }[] =
    [
      {
        value: "light",
        label: t("settings.theme_light"),
        icon: <Sun className="h-4 w-4" />,
      },
      {
        value: "dark",
        label: t("settings.theme_dark"),
        icon: <Moon className="h-4 w-4" />,
      },
      {
        value: "system",
        label: t("settings.theme_system"),
        icon: <Monitor className="h-4 w-4" />,
      },
    ];

  return (
    <div className="px-4 py-6 space-y-6">
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
        {t("settings.title")}
      </h2>

      {/* Profile Section */}
      <Card
        className="cursor-pointer hover:border-vajra-blue/50 dark:hover:border-vajra-blue/50 transition-colors border-gray-200 dark:border-[#1E3A5F]"
        onClick={() => navigate("/profile")}
      >
        <CardContent className="py-4 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-vajra-blue to-vajra-cyan overflow-hidden">
            {user?.user_metadata?.avatar_url ? (
              <img 
                src={user.user_metadata.avatar_url} 
                alt="Profilbild" 
                className="h-full w-full object-cover"
              />
            ) : (
              <User className="h-5 w-5 text-white" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {t("settings.profile")}
            </p>
            <p className="text-xs text-gray-500 dark:text-muted-foreground">
              {t("settings.profile_desc")}
            </p>
          </div>
          <span className="text-gray-400 dark:text-muted-foreground">
            &rsaquo;
          </span>
        </CardContent>
      </Card>

      <Separator className="bg-gray-100 dark:bg-[#1E3A5F]" />

      {/* Appearance */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
          {t("settings.appearance")}
        </h3>
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-4">
            <div className="flex gap-2">
              {themeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setTheme(opt.value); if (user?.id) syncAllSettings(user.id); }}
                  className={`
                    flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5
                    text-sm font-medium transition-all border
                    ${
                      theme === opt.value
                        ? "bg-vajra-blue/10 dark:bg-vajra-blue/20 border-vajra-blue/40 text-vajra-blue dark:text-vajra-cyan"
                        : "border-gray-200 dark:border-[#1E3A5F] text-gray-500 dark:text-muted-foreground hover:border-gray-400 dark:hover:border-gray-500"
                    }
                  `}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Language */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
          {t("settings.language")}
        </h3>
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => i18n.changeLanguage("de")}
                className={`
                  flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5
                  text-sm font-medium transition-all border
                  ${
                    currentLang === "de"
                      ? "bg-vajra-blue/10 dark:bg-vajra-blue/20 border-vajra-blue/40 text-vajra-blue dark:text-vajra-cyan"
                      : "border-gray-200 dark:border-[#1E3A5F] text-gray-500 dark:text-muted-foreground hover:border-gray-400 dark:hover:border-gray-500"
                  }
                `}
              >
                <Globe className="h-4 w-4" />
                DE
              </button>
              <button
                type="button"
                onClick={() => i18n.changeLanguage("en")}
                className={`
                  flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5
                  text-sm font-medium transition-all border
                  ${
                    currentLang === "en"
                      ? "bg-vajra-blue/10 dark:bg-vajra-blue/20 border-vajra-blue/40 text-vajra-blue dark:text-vajra-cyan"
                      : "border-gray-200 dark:border-[#1E3A5F] text-gray-500 dark:text-muted-foreground hover:border-gray-400 dark:hover:border-gray-500"
                  }
                `}
              >
                <Globe className="h-4 w-4" />
                EN
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kontinuierlicher Wechsel */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
          Kontinuierlicher Wechsel
        </h3>

        {/* Tugend */}
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-4 space-y-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-vajra-cyan/10 dark:bg-vajra-cyan/20">
                  <BookOpen className="h-4 w-4 text-vajra-cyan" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Tugend anzeigen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Eine Zehn Tugend der Reihe nach pro Innehalten
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateTugendRotationEnabled(!tugendRotationEnabled)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
                  tugendRotationEnabled ? "bg-vajra-blue" : "bg-gray-200 dark:bg-gray-700"
                )}
                role="switch"
                aria-checked={tugendRotationEnabled}
              >
                <span
                  className={cn(
                    "inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200",
                    tugendRotationEnabled ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Gelübde */}
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-vajra-blue/10 dark:bg-vajra-blue/20">
                  <BookOpen className="h-4 w-4 text-vajra-blue dark:text-vajra-cyan" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Gelübde anzeigen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Gelübde aus gewählten Kategorien im Journal
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateVowSlotEnabled(!vowSlotEnabled)}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200",
                  vowSlotEnabled ? "bg-vajra-blue" : "bg-gray-200 dark:bg-gray-700"
                )}
                role="switch"
                aria-checked={vowSlotEnabled}
              >
                <span
                  className={cn(
                    "inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200",
                    vowSlotEnabled ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </div>

            {vowSlotEnabled && (
              <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-[#1E3A5F]">
                {/* Categories — excl. zehn-tugenden */}
                <div className="space-y-2">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Kategorien</span>
                  <div className="space-y-1.5">
                    {VOW_CATEGORIES.filter((c) => c.id !== "ten-virtues").map((cat) => {
                      const isChecked = vowSlotCategories.includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() =>
                            updateVowSlotCategories(
                              isChecked
                                ? vowSlotCategories.filter((c) => c !== cat.id)
                                : [...vowSlotCategories, cat.id]
                            )
                          }
                          className={cn(
                            "w-full flex items-center gap-3 rounded-lg px-3 py-2 border text-left transition-colors",
                            isChecked
                              ? "border-vajra-blue/40 bg-vajra-blue/5 dark:bg-vajra-blue/10"
                              : "border-gray-200 dark:border-[#1E3A5F] hover:border-gray-300 dark:hover:border-gray-500"
                          )}
                        >
                          <span
                            className={cn(
                              "flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors",
                              isChecked
                                ? "border-vajra-blue bg-vajra-blue dark:border-vajra-cyan dark:bg-vajra-cyan"
                                : "border-gray-300 dark:border-gray-600"
                            )}
                          >
                            {isChecked && <Check className="h-2.5 w-2.5 text-white" />}
                          </span>
                          <p className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 min-w-0 truncate">
                            {cat.label}
                          </p>
                          <span className="text-xs text-gray-400 dark:text-muted-foreground shrink-0">
                            {cat.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Count */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Anzahl anzeigen</span>
                  <div className="flex items-center border border-gray-200 dark:border-border/50 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => updateVowSlotCount(Math.max(1, vowSlotCount - 1))}
                      className="w-8 h-8 flex items-center justify-center bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/80 text-gray-500 text-base"
                    >
                      −
                    </button>
                    <span className="w-10 h-8 flex items-center justify-center text-sm font-semibold text-gray-900 dark:text-white bg-white dark:bg-background border-x border-gray-200 dark:border-border/50 tabular-nums">
                      {vowSlotCount}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateVowSlotCount(Math.min(10, vowSlotCount + 1))}
                      className="w-8 h-8 flex items-center justify-center bg-gray-50 dark:bg-muted hover:bg-gray-100 dark:hover:bg-muted/80 text-gray-500 text-base"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Order */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Reihenfolge</span>
                  <div className="flex gap-2">
                    {(["sequential", "random"] as const).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => updateVowSlotOrder(opt)}
                        className={cn(
                          "px-3 py-1.5 rounded-lg text-xs font-medium border transition-all",
                          vowSlotOrder === opt
                            ? "bg-vajra-blue/10 dark:bg-vajra-blue/20 border-vajra-blue/40 text-vajra-blue dark:text-vajra-cyan"
                            : "border-gray-200 dark:border-[#1E3A5F] text-gray-500 dark:text-muted-foreground"
                        )}
                      >
                        {opt === "sequential" ? "Sequentiell" : "Zufällig"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Zeitslots */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
          Zeitslots
        </h3>
        <Card
          className="cursor-pointer hover:border-vajra-blue/50 dark:hover:border-vajra-blue/50 transition-colors border-gray-200 dark:border-[#1E3A5F]"
          onClick={() => navigate("/settings/timeslots")}
        >
          <CardContent className="py-4 flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-vajra-blue/10 dark:bg-vajra-blue/20">
              <Clock className="h-5 w-5 text-vajra-blue dark:text-vajra-cyan" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Zeiten
              </p>
              <p className="text-xs text-gray-500 dark:text-muted-foreground">
                Innehalten &amp; Erinnerungen konfigurieren
              </p>
            </div>
            <span className="text-gray-400 dark:text-muted-foreground">&rsaquo;</span>
          </CardContent>
        </Card>
      </div>

      {/* Install App */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
          {t("settings.install")}
        </h3>
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-4">
            {isInstalled ? (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 dark:bg-emerald-500/20">
                  <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                  App ist installiert
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB]/10 to-[#06B6D4]/10 dark:from-[#2563EB]/20 dark:to-[#06B6D4]/20">
                  <Smartphone className="h-5 w-5 text-vajra-blue dark:text-vajra-cyan" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    App installieren
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Fuer schnellen Zugriff, Push-Erinnerungen und Offline-Modus.
                  </p>

                  {/* Android: native install prompt */}
                  {canInstall && (
                    <Button
                      size="sm"
                      className="bg-vajra-blue hover:bg-vajra-blue/90"
                      onClick={install}
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      Zum Homescreen hinzufuegen
                    </Button>
                  )}

                  {/* iOS: button opens modal */}
                  {isIOS && !canInstall && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#2563EB]/40 text-vajra-blue dark:text-vajra-cyan"
                      onClick={() => setShowIOSSteps(true)}
                    >
                      <Download className="h-4 w-4 mr-1.5" />
                      Anleitung anzeigen
                    </Button>
                  )}

                  {/* Neither iOS nor Android prompt available */}
                  {!canInstall && !isIOS && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Öffne die App im Browser deines Geräts, um sie zu
                      installieren.
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Legal */}
      <div className="pt-2 text-center">
        <a
          href="/legal"
          className="text-xs text-gray-400 dark:text-muted-foreground/50 hover:text-gray-600 dark:hover:text-muted-foreground transition-colors"
        >
          Impressum &amp; Datenschutz
        </a>
      </div>

      {/* Sign Out */}
      <div className="pt-2">
        <Button
          variant="outline"
          className="w-full border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
          onClick={signOut}
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t("settings.logout")}
        </Button>
      </div>

      {/* Debug / Diagnose */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setShowDebug((p) => !p)}
          className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-muted-foreground/50 hover:text-gray-600 dark:hover:text-muted-foreground transition-colors"
        >
          <Bug className="h-3.5 w-3.5" />
          Diagnose
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showDebug ? "rotate-180" : ""}`} />
        </button>

        {showDebug && (
          <Card className="mt-2 border-gray-200 dark:border-[#1E3A5F] text-xs font-mono">
            <CardContent className="py-3 space-y-1.5 text-gray-600 dark:text-gray-400">
              <p><span className="text-gray-400">User-ID:</span> {user?.id ?? "—"}</p>
              <p><span className="text-gray-400">Email:</span> {user?.email ?? "—"}</p>
              <p><span className="text-gray-400">Heute (lokal):</span> {diag?.todayDate ?? "…"}</p>
              <p><span className="text-gray-400">Einträge heute:</span> {diag?.todayEntries ?? "…"}</p>
              <p><span className="text-gray-400">Einträge gesamt:</span> {diag?.totalEntries ?? "…"}</p>
              {diag && diag.uniqueDates.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-[#1E3A5F] space-y-0.5">
                  <p className="text-gray-400 mb-1">Alle Daten in DB:</p>
                  {diag.uniqueDates.map((row) => (
                    <p key={`${row.userId}|${row.date}`}>
                      {row.date} · {row.count} Eintr. · ID: …{row.userId.slice(-8)}
                    </p>
                  ))}
                </div>
              )}
              {diag && diag.totalEntries === 0 && (
                <p className="text-amber-500 dark:text-amber-400 mt-1">⚠ Datenbank ist leer!</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* iOS install instructions modal */}
      {showIOSSteps && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowIOSSteps(false)}
        >
          <div
            className="w-full max-w-sm bg-white dark:bg-[#0D1117] rounded-2xl p-6 shadow-2xl border border-gray-200 dark:border-[#1E3A5F]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-[#2563EB]/10 dark:bg-[#2563EB]/20 rounded-xl">
                  <Smartphone className="h-5 w-5 text-vajra-blue dark:text-vajra-cyan" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  App installieren
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowIOSSteps(false)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Safari hint */}
            <div className="mb-5 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40">
              <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                Nur in <strong>Safari</strong> möglich — Chrome, Firefox &amp;
                Co. unterstuetzen die Installation auf iPhone nicht.
              </p>
            </div>

            {/* Steps */}
            <ol className="space-y-4">
              <li className="flex items-center gap-4">
                <span className="shrink-0 w-8 h-8 rounded-xl bg-[#2563EB]/10 dark:bg-[#2563EB]/20 text-vajra-blue dark:text-vajra-cyan text-sm font-bold flex items-center justify-center">
                  1
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Teilen-Symbol tippen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Das Quadrat mit dem Pfeil nach oben in Safari
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="shrink-0 w-8 h-8 rounded-xl bg-[#2563EB]/10 dark:bg-[#2563EB]/20 text-vajra-blue dark:text-vajra-cyan text-sm font-bold flex items-center justify-center">
                  2
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    &bdquo;Zum Home-Bildschirm&ldquo;
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Im Menu nach unten scrollen
                  </p>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <span className="shrink-0 w-8 h-8 rounded-xl bg-[#2563EB]/10 dark:bg-[#2563EB]/20 text-vajra-blue dark:text-vajra-cyan text-sm font-bold flex items-center justify-center">
                  3
                </span>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    &bdquo;Hinzufuegen&ldquo; tippen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Oben rechts im Dialog bestaetigen
                  </p>
                </div>
              </li>
            </ol>

            <button
              type="button"
              onClick={() => setShowIOSSteps(false)}
              className="mt-6 w-full py-3 rounded-xl bg-vajra-blue hover:bg-vajra-blue/90 text-white text-sm font-semibold transition-colors"
            >
              Verstanden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
